import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from 'store';
import { useGetOnboardingChecklistQuery, useConfirmOnboardingTaskMutation, useClaimOnboardingPointsMutation, useDismissOnboardingMutation } from 'store/services/onboarding/onboardingApi';
import { useGetRecentArticlesQuery } from 'store/services/blog/blogApi';
import { OnboardingTask, OnboardingTaskKey } from 'types/interfaces/onboarding';
import { trackEvent } from 'utils/analytics';
import ProgressHeader from './components/ProgressHeader/ProgressHeader';
import OnboardingChecklist from './components/OnboardingChecklist/OnboardingChecklist';
import BlogPreview from './components/BlogPreview/BlogPreview';
import CompletionModal from './components/CompletionModal/CompletionModal';
import MobileVerificationModal from 'components/MobileVerificationModal/MobileVerificationModal';
import { RecentActivityCarousel } from 'components/RecentActivityCarousel';
import { useConfetti } from 'hooks/useConfetti';
import { SEOHelmet } from 'components/SEO';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { triggerMilestone } = useConfetti();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // API queries
  const {
    data: checklistData,
    isLoading: isChecklistLoading,
    isError: isChecklistError,
    refetch: refetchChecklist
  } = useGetOnboardingChecklistQuery(undefined, {
    // Always refetch when mounting to ensure fresh data after completing tasks elsewhere
    refetchOnMountOrArgChange: true,
  });

  const { data: recentArticles, isLoading: isArticlesLoading } = useGetRecentArticlesQuery(6);

  const [confirmTask, { isLoading: isConfirming }] = useConfirmOnboardingTaskMutation();
  const [claimPoints, { isLoading: isClaiming }] = useClaimOnboardingPointsMutation();
  const [dismissOnboarding] = useDismissOnboardingMutation();

  // Local state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showFirstClaimModal, setShowFirstClaimModal] = useState(false);
  const [firstClaimPoints, setFirstClaimPoints] = useState(0);
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const [completingTaskKey, setCompletingTaskKey] = useState<OnboardingTaskKey | null>(null);
  const [claimingTaskKey, setClaimingTaskKey] = useState<OnboardingTaskKey | null>(null);
  const [animatingTaskKey, setAnimatingTaskKey] = useState<OnboardingTaskKey | null>(null);

  // Onboarding dismissal state - localStorage as cache, API as source of truth
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    return localStorage.getItem('onboardingDismissed') === 'true';
  });
  const [isDismissing, setIsDismissing] = useState(false);

  // Use refs to track previous values without causing re-renders
  const previousCompletedCountRef = useRef<number | null>(null);
  const previousCompletedTasksRef = useRef<Set<OnboardingTaskKey>>(new Set());
  const hasInitializedRef = useRef(false);

  // Sync localStorage cache with API response (for cross-device sync)
  useEffect(() => {
    if (checklistData?.onboardingDismissedAt) {
      localStorage.setItem('onboardingDismissed', 'true');
      setOnboardingDismissed(true);
    }
  }, [checklistData?.onboardingDismissedAt]);

  // Track milestone achievements and auto-detected task completions
  useEffect(() => {
    if (checklistData?.summary && checklistData?.tasks) {
      const currentCount = checklistData.summary.completedTasks;
      const currentCompletedTasks = new Set(
        checklistData.tasks
          .filter(t => t.status === 'COMPLETED')
          .map(t => t.taskKey)
      );

      // Skip tracking on initial load, just store the initial state
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        previousCompletedCountRef.current = currentCount;
        previousCompletedTasksRef.current = currentCompletedTasks;
        return;
      }

      // Track newly completed auto-detected tasks (no toast - user claims manually)
      checklistData.tasks.forEach(task => {
        if (
          task.status === 'COMPLETED' &&
          task.detectionType === 'AUTO' &&
          !previousCompletedTasksRef.current.has(task.taskKey)
        ) {
          // Auto-detected task just completed - track for analytics only
          // User will manually claim points via the "Claim" button
          trackEvent('onboarding_task_detected', {
            task_key: task.taskKey,
            task_order: task.displayOrder,
            detection_type: 'auto',
          });
        }
      });

      const prevCount = previousCompletedCountRef.current ?? 0;

      // Check for 5/10 milestone
      if (prevCount < 5 && currentCount >= 5) {
        triggerMilestone('50_percent');
        // Toast removed - socket-based notification handles this
        trackEvent('onboarding_milestone', { milestone: '50_percent' });
      }

      // Check for 10/10 completion
      if (prevCount < 10 && currentCount === 10) {
        setShowCompletionModal(true);
        triggerMilestone('all_complete');
        trackEvent('onboarding_milestone', { milestone: 'all_complete' });
      }

      // Update refs for next comparison
      previousCompletedCountRef.current = currentCount;
      previousCompletedTasksRef.current = currentCompletedTasks;
    }
  }, [checklistData, triggerMilestone]);

  // Handle task start button click
  const handleStartTask = useCallback((task: OnboardingTask) => {
    if (task.status !== 'AVAILABLE') return;

    // Track task start event
    trackEvent('onboarding_task_started', {
      task_key: task.taskKey,
      task_title: task.title,
      action_type: task.action.type,
    });

    // Special handling for phone verification - open modal directly
    if (task.taskKey === 'VERIFY_PHONE') {
      setShowPhoneVerificationModal(true);
      return;
    }

    if (task.action.type === 'NAVIGATE') {
      navigate(task.action.target);
    } else if (task.action.type === 'EXTERNAL_LINK') {
      // For social follow tasks, open in new tab and set completing state
      window.open(task.action.target, '_blank', 'noopener,noreferrer');
      setCompletingTaskKey(task.taskKey);
    }
  }, [navigate]);

  // Handle social follow confirmation
  const handleConfirmSocialFollow = useCallback(async (taskKey: OnboardingTaskKey) => {
    try {
      const result = await confirmTask({ taskKey }).unwrap();

      // Get points from result, or fall back to task's pointsReward
      const task = checklistData?.tasks?.find(t => t.taskKey === taskKey);
      const pointsEarned = result.pointsAwarded ?? task?.pointsReward ?? 100;

      // Toast removed - socket-based notification handles this
      // Animation removed - should only fire when user clicks "Claim"

      // Track analytics
      trackEvent('onboarding_task_completed', {
        task_key: taskKey,
        points_awarded: pointsEarned,
      });

      // Reset completing state
      setCompletingTaskKey(null);
    } catch (error) {
      toast.error('Failed to confirm. Please try again.');
    }
  }, [confirmTask, checklistData?.tasks]);

  // Handle cancel social follow
  const handleCancelSocialFollow = useCallback(() => {
    setCompletingTaskKey(null);
  }, []);

  // Handle claim points for completed tasks
  const handleClaimPoints = useCallback(async (taskKey: OnboardingTaskKey) => {
    setClaimingTaskKey(taskKey);
    try {
      const result = await claimPoints({ taskKey }).unwrap();

      // Get points from result, or fall back to task's pointsReward
      const task = checklistData?.tasks?.find(t => t.taskKey === taskKey);
      const pointsEarned = result.pointsAwarded ?? task?.pointsReward ?? 100;

      // Check if this is the first reward claim
      const isFirstClaim = !localStorage.getItem('hasClaimedFirstReward');
      if (isFirstClaim) {
        // Show first claim celebration modal with confetti
        setFirstClaimPoints(pointsEarned);
        setShowFirstClaimModal(true);
        triggerMilestone('50_percent');
        localStorage.setItem('hasClaimedFirstReward', 'true');
        trackEvent('onboarding_first_claim', {
          task_key: taskKey,
          points_awarded: pointsEarned,
        });
      }
      // Regular toast removed - socket-based notification handles this

      // Trigger animation
      setAnimatingTaskKey(taskKey);
      setTimeout(() => setAnimatingTaskKey(null), 1200);

      // Track analytics
      trackEvent('onboarding_points_claimed', {
        task_key: taskKey,
        points_awarded: pointsEarned,
      });

      // Reset claiming state
      setClaimingTaskKey(null);
    } catch (error: any) {
      // Extract error message from RTK Query error
      const errorMessage = error?.data?.message
        || error?.data?.error
        || 'Failed to claim points. Please try again.';
      toast.error(errorMessage);
      setClaimingTaskKey(null);
    }
  }, [claimPoints, checklistData?.tasks, triggerMilestone]);

  // Handle animation complete (remove task from view)
  const handleAnimationComplete = useCallback((taskKey: OnboardingTaskKey) => {
    setAnimatingTaskKey(null);
  }, []);

  // Handle completion modal close
  const handleCompletionModalClose = useCallback(() => {
    setShowCompletionModal(false);
  }, []);

  // Handle first claim modal close
  const handleFirstClaimModalClose = useCallback(() => {
    setShowFirstClaimModal(false);
  }, []);

  // Handle start trading button
  const handleStartTrading = useCallback(() => {
    setShowCompletionModal(false);
    navigate('/sendcards');
  }, [navigate]);

  // Handle view points button
  const handleViewPoints = useCallback(() => {
    setShowCompletionModal(false);
    navigate('/transaction-history');
  }, [navigate]);

  // Handle phone verification modal close
  const handlePhoneVerificationClose = useCallback(() => {
    setShowPhoneVerificationModal(false);
  }, []);

  // Handle phone verification success - refetch checklist to update task status
  const handlePhoneVerificationSuccess = useCallback(() => {
    refetchChecklist();
  }, [refetchChecklist]);

  // Handle dismiss onboarding - call API then animate out
  const handleDismissOnboarding = useCallback(async () => {
    setIsDismissing(true);
    try {
      await dismissOnboarding().unwrap();
      localStorage.setItem('onboardingDismissed', 'true');
      trackEvent('onboarding_dismissed');
    } catch (error) {
      // Silently fail - user can try again
      console.error('Failed to dismiss onboarding:', error);
    }
    // Animation completes regardless of API success
    setTimeout(() => {
      setOnboardingDismissed(true);
      setIsDismissing(false);
    }, 400); // Match animation duration
  }, [dismissOnboarding]);

  return (
    <>
      <SEOHelmet
        title="Home - TradeMagic"
        description="Your TradeMagic dashboard. Track your onboarding progress, discover new articles, and start trading Magic: The Gathering cards."
        canonical="https://trademagic.gg/home"
        type="website"
      />
      <div className="home-page">
      <div className="home-page-header">
        <h1>Welcome back, {currentUser?.username || 'Trader'}!</h1>
      </div>

      <div className="home-page-content">
        {/* Progress Header - hidden when onboarding is dismissed */}
        {!onboardingDismissed && (() => {
          const pointsEarned = checklistData?.summary?.totalPointsEarned ?? 0;
          const rawTotalPossible = checklistData?.summary?.totalPointsPossible ?? 1000;
          // Safety: ensure max is always >= earned (defense in depth for admin point changes)
          const safeTotalPointsAvailable = Math.max(pointsEarned, rawTotalPossible);
          return (
            <ProgressHeader
              completedTasks={checklistData?.summary?.completedTasks ?? 0}
              totalTasks={checklistData?.summary?.totalTasks ?? 10}
              pointsEarned={pointsEarned}
              totalPointsAvailable={safeTotalPointsAvailable}
              isLoading={isChecklistLoading}
              isDismissing={isDismissing}
            />
          );
        })()}

        {/* Onboarding Checklist - hidden when dismissed */}
        {!onboardingDismissed && (
          <OnboardingChecklist
            tasks={checklistData?.tasks ?? []}
            isLoading={isChecklistLoading}
            isError={isChecklistError}
            onRetry={refetchChecklist}
            onStartTask={handleStartTask}
            onClaimPoints={handleClaimPoints}
            onConfirmSocialFollow={handleConfirmSocialFollow}
            onCancelSocialFollow={handleCancelSocialFollow}
            completingTaskKey={completingTaskKey}
            claimingTaskKey={claimingTaskKey}
            animatingTaskKey={animatingTaskKey}
            onAnimationComplete={handleAnimationComplete}
            isConfirming={isConfirming}
            isClaiming={isClaiming}
            onDismiss={handleDismissOnboarding}
            isDismissing={isDismissing}
          />
        )}

        {/* Recent Activity Carousel */}
        <RecentActivityCarousel />

        {/* Blog Preview Section */}
        <BlogPreview
          articles={recentArticles ?? []}
          isLoading={isArticlesLoading}
        />
      </div>

      {/* Completion Modal */}
      <CompletionModal
        isOpen={showCompletionModal}
        onClose={handleCompletionModalClose}
        totalPointsEarned={checklistData?.summary?.totalPointsEarned ?? 1000}
        onStartTrading={handleStartTrading}
        onViewPoints={handleViewPoints}
      />

      {/* First Claim Celebration Modal */}
      <CompletionModal
        isOpen={showFirstClaimModal}
        onClose={handleFirstClaimModalClose}
        mode="firstClaim"
        pointsEarned={firstClaimPoints}
        totalPointsEarned={0}
        onStartTrading={handleFirstClaimModalClose}
        onViewPoints={handleFirstClaimModalClose}
      />

      {/* Phone Verification Modal */}
      <MobileVerificationModal
        isOpen={showPhoneVerificationModal}
        onClose={handlePhoneVerificationClose}
        onVerificationSuccess={handlePhoneVerificationSuccess}
      />
    </div>
    </>
  );
};

export default HomePage;
